import React, { useState } from 'react';
import { 
  Box, 
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar
} from '@mui/material';
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon, CalendarPlus } from 'lucide-react';
import dayjs from 'dayjs';

function TournamentCalendar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentMonth, setCurrentMonth] = useState(dayjs('2025-03-01')); // Start with March 2025
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const handlePrevMonth = () => setCurrentMonth(prev => prev.subtract(1, 'month'));
  const handleNextMonth = () => setCurrentMonth(prev => prev.add(1, 'month'));

  // Get events for current month
  const getEventsForMonth = (month) => {
    const monthNum = month.month() + 1; // dayjs months are 0-indexed
    const year = month.year();
    
    // Tournament events - these repeat monthly
    const tournaments = [
      { 
        id: 'frys-blitz',
        title: 'FRYS Blitz', 
        time: '18:30', 
        days: [5, 12, 19, 26],
        type: 'tournament',
        description: 'Win cash prizes, league points and the top 9 get R500 seats to Sunday\'s live game!',
        location: 'FRYS Poker Club',
        buyIn: 'R300',
        startingChips: '50,000',
        reEntry: 'R300 / 50,000 chips',
        addOn: 'R300 / 100,000 chips',
        luckyDip: 'R100 / 50,000 chips',
        blindLevels: '15 minutes',
        blindStructure: 'Big blind ante',
        lateReg: '7 levels',
        dealers: true,
        foodAndDrinks: true,
        surrenderStack: true,
        paymentOptions: 'Cash and card accepted',
        registrationLink: 'https://forms.gle/p66wwRjL95yTgPsY7',
        logo: '/images/frys-poker-logo.png'
      },
      { 
        id: 'concept-turbo',
        title: 'Concept R600 LIVE TURBO', 
        time: '18:30', 
        days: [6, 13, 20, 27], 
        type: 'tournament',
        description: 'Fast-paced turbo tournament with 15-minute blind levels. Includes 2 lives for rebuys/add-ons.',
        location: 'Tournament Area',
        buyIn: 'R600',
        startingChips: '30,000',
        rebuy: '30,000',
        addOn: '60,000 (120,000 if both lives retained)',
        lifeline: 'R200 for 60,000 chips (optional)',
        blindLevels: '15 minutes',
        dealers: true,
        foodAndDrinks: true,
        registrationLink: 'https://conceptpoker.co.za/register',
        logo: '/images/concept-poker-logo.png'
      }
    ];

    // Cash game events
    const cashGames = [
      { 
        id: 'cash-game',
        title: 'Rounders 10/20/40 PLOS or 10/20 Mixed',
        time: '15:00',
        days: [0, 1, 2, 3, 4, 5, 6], // Running daily
        type: 'cash',
        description: 'Daily cash games with multiple stakes and game variations. Professional dealers and comfortable seating.',
        location: 'Rounders Poker Club',
        stakes: 'PLOS 10/20/40 or Mixed Games 10/20',
        minBuyIn: 'R2,000',
        maxBuyIn: 'R8,000',
        tableFee: 'R200 per hour',
        dealers: true,
        foodAndDrinks: true,
        reservations: 'Call or text 072-555-1234',
        paymentOptions: 'Cash only',
        logo: '/images/rounders-poker-logo.png'
      }
    ];

    return [...tournaments, ...cashGames];
  };

  const events = getEventsForMonth(currentMonth);

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return events.filter(event => event.days.includes(day));
  };

  // Generate calendar grid data
  const generateCalendarData = (month) => {
    const firstDayOfMonth = month.startOf('month').day(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = month.daysInMonth();
    
    // Adjust for Monday as first day of week (shift Sunday to end)
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Create weeks array
    const weeks = [];
    let currentWeek = Array(7).fill(null);
    
    // Fill in the first week with empty days before the 1st
    for (let i = adjustedFirstDay; i < 7; i++) {
      currentWeek[i] = i - adjustedFirstDay + 1;
    }
    weeks.push(currentWeek);
    
    // Fill in the rest of the weeks
    let dayCounter = 7 - adjustedFirstDay + 1;
    while (dayCounter <= daysInMonth) {
      currentWeek = Array(7).fill(null);
      for (let i = 0; i < 7 && dayCounter <= daysInMonth; i++) {
        currentWeek[i] = dayCounter++;
      }
      weeks.push(currentWeek);
    }
    
    return weeks;
  };

  // For mobile view, get all days with events
  const getDaysWithEvents = () => {
    const daysInMonth = currentMonth.daysInMonth();
    const daysWithEvents = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(day);
      if (dayEvents.length > 0) {
        daysWithEvents.push({
          day,
          date: currentMonth.date(day).format('ddd, MMM D'),
          events: dayEvents
        });
      }
    }
    
    return daysWithEvents;
  };

  const calendarData = generateCalendarData(currentMonth);
  
  const handleEventClick = (event, day) => {
    // Create a copy of the event with the specific day
    const eventWithDay = {
      ...event,
      specificDay: day,
      specificDate: currentMonth.date(day).format('MMMM D, YYYY')
    };
    setSelectedEvent(eventWithDay);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Render desktop calendar view
  const renderDesktopCalendar = () => (
    <>
      {/* Day Headers */}
      <Grid container>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <Grid item xs={12/7} key={day} sx={{ 
            p: 1.5,
            textAlign: 'center',
            fontWeight: 'medium',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            bgcolor: '#1a1a2e'
          }}>
            {day}
          </Grid>
        ))}
      </Grid>

      {/* Calendar Weeks */}
      {calendarData.map((week, weekIndex) => (
        <Grid container key={`week-${weekIndex}`}>
          {week.map((day, dayIndex) => (
            <Grid item xs={12/7} key={`day-${weekIndex}-${dayIndex}`} sx={{ 
              borderRight: '1px solid rgba(255,255,255,0.1)',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              p: 1,
              height: '150px',
              position: 'relative',
              bgcolor: '#0f0f1e',
              overflow: 'hidden'
            }}>
              {day && (
                <>
                  <Typography sx={{ 
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    color: '#aaa',
                    fontSize: '0.875rem'
                  }}>
                    {day}
                  </Typography>
                  
                  <Box sx={{ 
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    maxHeight: 'calc(100% - 30px)',
                    overflow: 'auto'
                  }}>
                    {getEventsForDay(day).sort((a, b) => {
                      const timeA = a.time.split(':').map(Number);
                      const timeB = b.time.split(':').map(Number);
                      
                      if (timeA[0] !== timeB[0]) {
                        return timeA[0] - timeB[0];
                      }
                      
                      return timeA[1] - timeB[1];
                    }).map((event, eventIndex) => (
                      <Box
                        key={`${day}-${event.title}-${eventIndex}`}
                        onClick={() => handleEventClick(event, day)}
                        sx={{
                          bgcolor: event.type === 'cash' 
                            ? '#854d0e' 
                            : '#4a148c',
                          color: 'white',
                          p: 0.75,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          border: event.type === 'cash' 
                            ? '1px solid #fbbf24'
                            : '1px solid #6d1b9b',
                          boxShadow: event.type === 'cash'
                            ? '0 0 6px rgba(251, 191, 36, 0.5)'
                            : '0 0 6px rgba(109, 27, 155, 0.5)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: event.type === 'cash'
                              ? '#a16207'
                              : '#5c1799',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Typography sx={{ 
                          fontWeight: 'bold',
                          fontSize: 'inherit',
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {event.title}
                        </Typography>
                        <Typography 
                          sx={{ 
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5
                          }}
                        >
                          🕒 {event.time}
                        </Typography>
                        {event.logo && (
                          <Avatar 
                            src={event.logo} 
                            alt={event.title}
                            sx={{ 
                              width: 32, 
                              height: 32,
                              ml: 1,
                              bgcolor: 'transparent' 
                            }}
                            variant="rounded"
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Grid>
          ))}
        </Grid>
      ))}
    </>
  );

  // Render mobile calendar view (list format)
  const renderMobileCalendar = () => {
    const daysWithEvents = getDaysWithEvents();
    
    return (
      <List sx={{ p: 0 }}>
        {daysWithEvents.map(({ day, date, events }) => (
          <React.Fragment key={`mobile-day-${day}`}>
            <ListItem 
              sx={{ 
                bgcolor: '#1a1a2e', 
                py: 1.5,
                px: 2
              }}
            >
              <ListItemText 
                primary={date} 
                primaryTypographyProps={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}
              />
            </ListItem>
            
            {events.sort((a, b) => {
              const timeA = a.time.split(':').map(Number);
              const timeB = b.time.split(':').map(Number);
              
              if (timeA[0] !== timeB[0]) {
                return timeA[0] - timeB[0];
              }
              
              return timeA[1] - timeB[1];
            }).map((event, index) => (
              <ListItem 
                key={`mobile-event-${day}-${index}`}
                onClick={() => handleEventClick(event, day)}
                sx={{ 
                  bgcolor: '#0f0f1e',
                  py: 1.5,
                  px: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <Typography 
                      sx={{ 
                        fontWeight: 'bold',
                        color: event.type === 'cash' ? '#fbbf24' : '#a78bfa',
                        fontSize: '0.9rem'
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography 
                      sx={{ 
                        color: 'white',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      🕒 {event.time}
                    </Typography>
                  </Box>
                  <Typography 
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.8rem',
                      mt: 0.5
                    }}
                  >
                    {event.type === 'tournament' ? 'Tournament' : 'Cash Game'}
                  </Typography>
                </Box>
              </ListItem>
            ))}
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Paper sx={{ 
      bgcolor: '#1a1a2e', 
      borderRadius: 0,
      overflow: 'hidden',
      boxShadow: 'none'
    }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <IconButton 
          onClick={handlePrevMonth}
          sx={{ color: 'white' }}
        >
          <ChevronLeft />
        </IconButton>
        
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {currentMonth.format('MMMM YYYY').toUpperCase()}
        </Typography>
        
        <IconButton 
          onClick={handleNextMonth}
          sx={{ color: 'white' }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box>
        {isMobile ? renderMobileCalendar() : renderDesktopCalendar()}
      </Box>
      
      {/* Event Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ 
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              {selectedEvent.logo && (
                <Avatar 
                  src={selectedEvent.logo} 
                  alt={selectedEvent.title}
                  sx={{ 
                    width: 48, 
                    height: 48,
                    bgcolor: 'transparent' 
                  }}
                />
              )}
              <Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {selectedEvent.title}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {selectedEvent.specificDate} at {selectedEvent.time}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
              <Typography paragraph sx={{ mb: 3 }}>
                {selectedEvent.description}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Location
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedEvent.location}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Buy-in
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedEvent.buyIn}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Starting Chips
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedEvent.startingChips}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Blind Levels
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedEvent.blindLevels}
                  </Typography>
                </Grid>
                
                {selectedEvent.rebuy && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Rebuy
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedEvent.rebuy} chips
                    </Typography>
                  </Grid>
                )}
                
                {selectedEvent.addOn && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Add-on
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedEvent.addOn}
                    </Typography>
                  </Grid>
                )}
                
                {selectedEvent.lifeline && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Lifeline
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedEvent.lifeline}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Additional Information
                </Typography>
                <Typography variant="body2" paragraph>
                  {selectedEvent.dealers && "• Professional dealers provided"}
                </Typography>
                <Typography variant="body2" paragraph>
                  {selectedEvent.foodAndDrinks && "• Food and drinks available"}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
              {selectedEvent.registrationLink && (
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<CalendarPlus size={18} />}
                  href={selectedEvent.registrationLink}
                  target="_blank"
                  sx={{ 
                    mr: 'auto',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  Register Now
                </Button>
              )}
              <Button 
                onClick={handleCloseDialog}
                variant="text"
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
}

export default TournamentCalendar; 